import { createPdfFromJpeg, flattenImage, PDF_PAGE_SIZES } from '@spazious/dollhouse-utils';

export enum CaptureFormatEnum {
  PNG = 'png',
  JPG = 'jpg',
  PDF = 'pdf',
}

export interface CaptureFormat {
  label: string;
  description: string;
  extension: string;
}

export const CAPTURE_FORMATS: Record<CaptureFormatEnum, CaptureFormat> = {
  [CaptureFormatEnum.PNG]: {
    label: 'PNG',
    description: 'High quality image, best for sharing online.',
    extension: 'png',
  },
  [CaptureFormatEnum.JPG]: {
    label: 'JPG',
    description: 'Smaller image file, best for sending by email.',
    extension: 'jpg',
  },
  [CaptureFormatEnum.PDF]: {
    label: 'PDF',
    description: 'A4 document, ready to print.',
    extension: 'pdf',
  },
};

const JPEG_QUALITY = 0.92;

// 15 mm in points: keeps the whole image printable on A4 and US Letter paper
const PDF_MARGIN = (15 / 25.4) * 72;

// Paper does not show detail beyond print resolution, so PDFs embed the capture
// scaled down to 300 dpi over the printable area: larger images only make the
// file heavier (detailed scenes would otherwise weigh tens of MB)
const PDF_DPI = 300;
const PDF_IMAGE_MAX_SIZE = PDF_PAGE_SIZES.A4.map((side) =>
  Math.floor(((side - 2 * PDF_MARGIN) / 72) * PDF_DPI)
) as [number, number];

/**
 * Encode a PNG scene capture into the requested download format
 */
export const encodeCapture = async (png: Blob, format: CaptureFormatEnum): Promise<Blob> => {
  if (format === CaptureFormatEnum.PNG) {
    return png;
  }

  if (format === CaptureFormatEnum.JPG) {
    const jpeg = await flattenImage(png, { type: 'image/jpeg', quality: JPEG_QUALITY });
    return jpeg.blob;
  }

  const jpeg = await flattenImage(png, {
    type: 'image/jpeg',
    quality: JPEG_QUALITY,
    maxSize: PDF_IMAGE_MAX_SIZE,
  });

  const bytes = new Uint8Array(await jpeg.blob.arrayBuffer());
  const pdf = createPdfFromJpeg(bytes, jpeg.width, jpeg.height, {
    pageSize: PDF_PAGE_SIZES.A4,
    margin: PDF_MARGIN,
  });
  return new Blob([pdf], {
    type: 'application/pdf',
  });
};
